import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useMentionNotifications } from "@/hooks/use-mention-notifications";
import { CommentInput } from "./CommentInput";
import { CommentsList } from "./CommentsList";
import { Comment } from "./CommentItem";
import { useIsMobile } from "@/hooks/use-mobile";

interface CommentSectionProps {
  promptId: string;
  onNewComment?: () => void;
}

export function CommentSection({ promptId, onNewComment }: CommentSectionProps) {
  const { user, userProfile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ id: string; username: string } | null>(null);
  const { notifyMentionedUsers } = useMentionNotifications();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (promptId) {
      fetchComments();
    }
  }, [promptId]);

  const fetchComments = async () => {
    if (!promptId) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from("prompt_comments")
        .select(`
            id,
            content,
            created_at,
            user_id,
            parent_id,
            profiles (
              username,
              avatar_id
            )
          `)
          .eq("prompt_id", promptId)
          .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching comments:", error);
        setComments([]);
        return;
      }

      if (data && Array.isArray(data)) {
        // Transform and organize into threaded structure
        const allComments = data
          .filter((comment: any) => comment?.id)
          .map((comment: any) => ({
            id: comment.id,
            content: comment.content,
            created_at: comment.created_at,
            user_id: comment.user_id,
            parent_id: comment.parent_id,
            profiles: comment.profiles 
              ? (Array.isArray(comment.profiles) ? comment.profiles[0] : comment.profiles)
              : { username: "Unknown", avatar_id: 1 },
            replies: [] as Comment[],
          })) as Comment[];

        // Build threaded structure
        const commentMap = new Map<string, Comment>();
        const rootComments: Comment[] = [];

        // First pass: create map
        allComments.forEach(comment => {
          commentMap.set(comment.id, { ...comment, replies: [] });
        });

        // Second pass: build tree
        allComments.forEach(comment => {
          const mappedComment = commentMap.get(comment.id)!;
          if (comment.parent_id && commentMap.has(comment.parent_id)) {
            const parent = commentMap.get(comment.parent_id)!;
            parent.replies = parent.replies || [];
            parent.replies.push(mappedComment);
          } else {
            rootComments.push(mappedComment);
          }
        });

        // Sort root comments by newest first, replies by oldest first (chronological)
        rootComments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        setComments(rootComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = (commentId: string, username: string) => {
    setReplyingTo({ id: commentId, username });
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleSubmitComment = async (content: string) => {
    if (!user) {
      toast.error("Please sign in to comment");
      return;
    }
    
    if (!promptId) {
      toast.error("Invalid prompt");
      return;
    }

    setSubmitting(true);
    try {
      const { data: commentData, error } = await supabase
        .from("prompt_comments")
        .insert({
          prompt_id: promptId,
          user_id: user.id,
          content: content,
          parent_id: replyingTo?.id || null,
        })
        .select()
        .single();

      if (error) throw error;

      if (commentData) {
        // Notify mentioned users
        if (userProfile?.username) {
          await notifyMentionedUsers(
            content,
            user.id,
            userProfile.username,
            promptId
          );
        }

        setReplyingTo(null);
        fetchComments();
        onNewComment?.();
        toast.success(replyingTo ? "Reply added!" : "Comment added!");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Comment Input - at top on desktop, fixed on mobile */}
      <CommentInput
        onSubmit={handleSubmitComment}
        isSubmitting={submitting}
        isFixed={isMobile}
        replyingTo={replyingTo}
        onCancelReply={handleCancelReply}
      />

      {/* Comments List */}
      <div className={isMobile ? "pb-24" : ""}>
        <CommentsList 
          comments={comments} 
          loading={loading}
          onReply={handleReply}
        />
      </div>
    </div>
  );
}
